import { activityService } from '../services/activityService';
import {
  aggregateCallMetrics,
  callMatchesUser,
  normalizeCallOutcomeSlug,
  CALL_OUTCOME_LABELS,
  CALL_OUTCOME_SLUGS,
  isTrackedCall,
} from './callOutcomeAnalytics';

export const RANGE_OPTS = [
  { id: 'heute', label: 'Heute', days: 1 },
  { id: '7d', label: '7 Tage', days: 7 },
  { id: '30d', label: '30 Tage', days: 30 },
  { id: '90d', label: '90 Tage', days: 90 },
];

export const MATRIX_OUTCOME_COLS = [
  { slug: 'total', label: 'Anrufe', key: 'total' },
  { slug: 'reached_appt', label: 'Termine', key: 'termin' },
  { slug: 'gatekeeper', label: 'GK', key: 'gatekeeper' },
  { slug: 'decision_maker', label: 'Entsch.', key: 'decider' },
  { slug: 'reached_followup', label: 'Follow-up', key: 'followup' },
  { slug: 'callback', label: 'Rückruf', key: 'callback' },
  { slug: 'not_voicemail', label: 'VM', key: 'voicemail' },
  { slug: 'reached_no', label: 'Absage', key: 'noInterest' },
];

export const DEFAULT_WIDGET_IDS = ['motivation', 'kpis', 'rep_table', 'outcomes', 'by_user', 'daily_chart', 'agent_cards'];

export function loadActivityDashboardWidgets() {
  try {
    const s = localStorage.getItem('crm_activity_dashboard_widgets');
    if (s) return JSON.parse(s);
  } catch { /* ignore */ }
  return DEFAULT_WIDGET_IDS;
}

export function saveActivityDashboardWidgets(ids) {
  try {
    localStorage.setItem('crm_activity_dashboard_widgets', JSON.stringify(ids));
  } catch { /* ignore */ }
}

export const DEFAULT_WIDGET_CONFIG = [
  { id: 'motivation', size: 'full', visible: true },
  { id: 'kpis', size: 'full', visible: true },
  { id: 'rep_table', size: 'full', visible: true },
  { id: 'agent_cards', size: 'full', visible: true },
  { id: 'outcomes', size: 'full', visible: true },
  { id: 'by_user', size: 'half', visible: true },
  { id: 'daily_chart', size: 'half', visible: true },
];

export function loadWidgetConfig() {
  try {
    const s = localStorage.getItem('crm_activity_widget_config_v2');
    if (s) {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Merge in any new default widgets not yet in saved config
        const missing = DEFAULT_WIDGET_CONFIG.filter(d => !parsed.find(p => p.id === d.id));
        return [...parsed, ...missing];
      }
    }
  } catch { /* ignore */ }
  return DEFAULT_WIDGET_CONFIG.map(w => ({ ...w }));
}

export function saveWidgetConfig(config) {
  try {
    localStorage.setItem('crm_activity_widget_config_v2', JSON.stringify(config));
  } catch { /* ignore */ }
}

export function parseDeDate(str) {
  if (!str) return null;
  const p = String(str).split('.');
  if (p.length === 3) return new Date(+p[2], +p[1] - 1, +p[0]);
  return null;
}

export function actDate(a) {
  if (a?.ts) return new Date(a.ts);
  if (a?.date) return parseDeDate(a.date) || new Date(a.date);
  return null;
}

export function toYMD(d) {
  return d.toISOString().slice(0, 10);
}

export function computeRange(rangeId) {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const opt = RANGE_OPTS.find(r => r.id === rangeId) || RANGE_OPTS[2];
  const days = opt.days;
  const start = new Date(today);
  start.setDate(start.getDate() - (days - 1));
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  prevStart.setHours(0, 0, 0, 0);
  return { start, end: now, prevStart, prevEnd, days };
}

export function weekBounds(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offsetWeeks * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday, end: sunday };
}

export function flattenCalls(leads = []) {
  const all = activityService.getAll();
  const result = [];
  Object.entries(all).forEach(([leadId, acts]) => {
    const lead = leads.find(l => String(l.id) === String(leadId));
    (acts || []).filter(a => isTrackedCall(a)).forEach(a => {
      result.push({ ...a, leadId, lead });
    });
  });
  return result.sort((a, b) => new Date(b.ts || b.date || 0) - new Date(a.ts || a.date || 0));
}

export function filterCalls(calls, { start, end, userId = 'all', outcomeSlug = null, authUsers = [] } = {}) {
  return calls.filter(c => {
    const d = actDate(c);
    if (start && end && d && (d < start || d > end)) return false;
    if (userId && userId !== 'all' && !callMatchesUser(c, userId, authUsers)) return false;
    if (outcomeSlug && outcomeSlug !== 'total' && normalizeCallOutcomeSlug(c) !== outcomeSlug) return false;
    return true;
  });
}

export function countCallsToday(calls, authUsers, userId = 'all') {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return filterCalls(calls, { start: today, end, userId, authUsers }).length;
}

export function weekCallStats(calls, authUsers, userId = 'all') {
  const thisW = weekBounds(0);
  const lastW = weekBounds(-1);
  const thisCalls = filterCalls(calls, { start: thisW.start, end: thisW.end, userId, authUsers });
  const lastCalls = filterCalls(calls, { start: lastW.start, end: lastW.end, userId, authUsers });
  const m = aggregateCallMetrics(thisCalls);
  const pm = aggregateCallMetrics(lastCalls);
  return { thisCalls, lastCalls, m, pm };
}

export function buildRepRows(calls, authUsers, isAdmin, currentUser) {
  const users = isAdmin && authUsers.length > 0
    ? authUsers
    : (currentUser ? [currentUser] : []);

  return users.map((u, idx) => {
    const myCalls = calls.filter(c => callMatchesUser(c, u.id, authUsers));
    const m = aggregateCallMetrics(myCalls);
    const byOutcome = {};
    myCalls.forEach(c => {
      const slug = normalizeCallOutcomeSlug(c);
      byOutcome[slug] = (byOutcome[slug] || 0) + 1;
    });
    return {
      id: u.id,
      label: u.name || u.email || u.id,
      idx,
      metrics: m,
      byOutcome,
      calls: myCalls,
    };
  }).sort((a, b) => b.metrics.total - a.metrics.total);
}

export function getMatrixCellCount(row, col) {
  if (col.slug === 'total') return row.metrics.total;
  if (col.key && row.metrics[col.key] != null) return row.metrics[col.key];
  return row.byOutcome[col.slug] || 0;
}

export function outcomeLabel(slug) {
  return CALL_OUTCOME_LABELS[slug] || slug;
}

export { CALL_OUTCOME_SLUGS, CALL_OUTCOME_LABELS };
